import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { apiSlice, BuildingReadModel, useDeleteSpacesByIdMutation } from '@client/api';
import { Button, Modal, ModalContent, ModalFooter, Typography } from '@client/toolkit';
import { useAppDispatch } from '../../../../../hooks';
import { PortfolioPaths } from '../../../portfolio.router';

interface BuildingDeleteModalProps {
  building: BuildingReadModel;
  open: boolean;
  onCancel: () => void;
  onDelete: () => void;
}

export const BuildingDeleteModal = ({ building, open, onCancel, onDelete }: BuildingDeleteModalProps) => {
  const { t } = useTranslation('portfolio');
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [mutation, { isLoading: isDeletingBuilding }] = useDeleteSpacesByIdMutation();

  const handleDeleteBuilding = async () => {
    if (building?.id == null) return;

    try {
      await mutation({ id: building.id }).unwrap();

      // Navigate to buildings list after successful deletion
      navigate(PortfolioPaths.BuildingList, { replace: true });

      // Reset API state to clear stale cache and cancel pending queries
      dispatch(apiSlice.util.resetApiState());

      onDelete();
    } catch {
      //empty on purpose
    }
  };

  const handleClose = () => {
    onCancel();
  };

  return (
    <Modal open={open} onClose={handleClose} onBackdropClick={handleClose} closeDisabled={isDeletingBuilding}>
      <ModalContent>
        <div className="uppercase text-xs font-semibold p-4 text-center text-white">{t('building.deleteTitle')}</div>
        <Typography align="center" className="px-4 py-4 text-white">
          <Trans
            t={t}
            i18nKey={'building.deleteConfirm'}
            values={{ name: building?.name }}
            components={{ bold: <strong /> }}
          />
        </Typography>
      </ModalContent>
      <ModalFooter className="justify-center">
        <Button color="danger" onClick={handleDeleteBuilding} progressing={isDeletingBuilding}>
          {tCommon('delete')}
        </Button>
        <Button color="primary-dark" onClick={handleClose}>
          {tCommon('cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
